import type { WorkflowRunSummaryPayload } from '@Application/WorkflowRuns/BeneficiaryWorkflowRuns/Actions/beneficiaryWorkflowRunsFetched';
import type { WorkflowRunPayload } from '@Application/WorkflowRuns/WorkflowRun/Actions/workflowRunFetched';
import LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';
import WorkflowRunDocumentView from '@UI/Store/WorkflowRuns/Views/WorkflowRunDocumentView';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * One row of the dossier's run list. `fromRunPayload` projects a full run onto its row, so the lists
 * follow every response that returns one.
 */
class WorkflowRunSummaryView {
	readonly id: number;
	readonly workflowKey: string;
	readonly label: string;
	readonly kind: LabeledValueView;
	readonly state: LabeledValueView;
	readonly openedAt: Date;
	/** Meaningless while the run is open: read `launchedAt` instead. */
	readonly startedAt: Date;
	readonly completedAt: Date | null;
	readonly isGenerating: boolean;
	readonly handledAt: Date | null;
	readonly documents: readonly WorkflowRunDocumentView[];

	constructor(
		id: number,
		workflowKey: string,
		label: string,
		kind: LabeledValueView,
		state: LabeledValueView,
		openedAt: Date,
		startedAt: Date,
		completedAt: Date | null,
		isGenerating: boolean,
		handledAt: Date | null,
		documents: readonly WorkflowRunDocumentView[],
	) {
		this.id = id;
		this.workflowKey = workflowKey;
		this.label = label;
		this.kind = kind;
		this.state = state;
		this.openedAt = openedAt;
		this.startedAt = startedAt;
		this.completedAt = completedAt;
		this.isGenerating = isGenerating;
		this.handledAt = handledAt;
		this.documents = documents;

		Object.freeze(this);
	}

	static fromPayload(payload: WorkflowRunSummaryPayload): WorkflowRunSummaryView {
		return new WorkflowRunSummaryView(
			payload.id,
			payload.workflowKey,
			payload.label,
			new LabeledValueView(payload.kind),
			new LabeledValueView(payload.state),
			new Date(payload.openedAt),
			new Date(payload.startedAt),
			payload.completedAt !== null ? new Date(payload.completedAt) : null,
			payload.isGenerating,
			payload.handledAt !== null ? new Date(payload.handledAt) : null,
			payload.documents.map((document) => new WorkflowRunDocumentView(document)),
		);
	}

	static fromRunPayload(payload: WorkflowRunPayload): WorkflowRunSummaryView {
		return WorkflowRunSummaryView.fromPayload({
			id: payload.id,
			workflowKey: payload.workflowKey,
			label: payload.label,
			kind: payload.kind,
			state: payload.state,
			openedAt: payload.openedAt,
			startedAt: payload.startedAt,
			completedAt: payload.completedAt,
			isGenerating: payload.isGenerating,
			handledAt: payload.handledAt,
			documents: payload.documents,
		});
	}

	/** Generation not launched yet: the run still receives sources. */
	get isOpen(): boolean {
		return this.state.value === 'open';
	}

	get isCompleted(): boolean {
		return this.state.value === 'completed';
	}

	/** At least one document waits for the reviewer. */
	get hasDocumentsToReview(): boolean {
		return this.documents.some((document) => document.isToReview);
	}

	/** When the generation was launched; `null` while the run is open. */
	get launchedAt(): Date | null {
		return this.isOpen ? null : this.startedAt;
	}
}

export default WorkflowRunSummaryView;
